import * as React from "react";
import { Link, useLocation } from "@reach/router";
import { useTranslation } from "react-i18next";

import { useFeatureFlagEnabled } from "src/hooks/useFeatureFlagEnabled";
import { PROFILE_STATS_FLAG } from "src/featureFlags";
import { HELP_SCREEN_IDS } from "src/components/HelpSection/helpScreens";

import { ReactComponent as HelpSVG } from "../../icons/help.svg";
import { ReactComponent as RestartSVG } from "../../icons/refresh.svg";
import { ReactComponent as SettingsSVG } from "../../icons/settings-gear.svg";
import { ReactComponent as GameSVG } from "../../icons/four_seeds.svg";
import { ReactComponent as HistorySVG } from "../../icons/history.svg";
import { ReactComponent as HomeSVG } from "../../icons/home.svg";
import { ReactComponent as StatsSVG } from "../../icons/stats.svg";
import { ReactComponent as CloseSVG } from "../../icons/close.svg";
import type { Theme } from "../../theme";

type Props = {
  onClose: () => void;
};

const menuHeaderStyle = ({ gridUnit }: Theme) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: `${gridUnit * 2}px`,
  borderBottom: `2px solid #e0e0e0`
});

const menuTitleStyle = ({ gridUnit }: Theme) => ({
  fontSize: gridUnit * 4,
  fontWeight: "bold",
  margin: 0
});

const closeButtonStyle = {
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "4px",
  transition: "background-color 0.2s",
  ":hover": {
    backgroundColor: "#f0f0f0"
  },
  "& svg": {
    width: "24px",
    height: "24px"
  }
};

const menuSectionStyle = {
  padding: "8px 0"
};

const sectionHeaderStyle = {
  padding: "12px 20px 8px",
  fontSize: "12px",
  fontWeight: "600",
  color: "#666",
  textTransform: "uppercase" as const,
  letterSpacing: "0.5px"
};

const menuItemStyle = {
  display: "flex",
  alignItems: "center",
  padding: "12px 20px",
  textDecoration: "none",
  color: "#333",
  transition: "background-color 0.2s",
  cursor: "pointer",
  ":hover": {
    backgroundColor: "#f5f5f5"
  },
  "&.active": {
    backgroundColor: "#e8f4f8",
    borderLeft: "4px solid #1976d2",
    paddingLeft: "16px",
    "& svg": {
      width: "20px",
      height: "20px",
      marginRight: "16px",
      flexShrink: 0
    }
  },
  "& svg": {
    width: "20px",
    height: "20px",
    marginRight: "16px",
    flexShrink: 0
  }
};

const menuItemTextStyle = {
  fontSize: "16px",
  fontWeight: "500"
};

const dividerStyle = {
  height: "1px",
  backgroundColor: "#e0e0e0",
  margin: "8px 0"
};

const AppMenu = ({ onClose }: Props): React.ReactNode => {
  const location = useLocation();
  const { t } = useTranslation();
  const profileStatsEnabled = useFeatureFlagEnabled(PROFILE_STATS_FLAG);
  const isInGame = location.pathname.includes("/game");
  const isInMultiplayer = location.pathname.includes("/multiplayer");
  const isOnHelpPage = location.pathname.includes("/game/help");
  const gameMode = isInMultiplayer ? "multiplayer" : "singleplayer";

  // Check active state for each route
  const isHomePage = location.pathname === "/";
  const isGameBoardPage = location.pathname === "/game";
  const isSettingsPage = location.pathname.includes("/settings");
  const isHistoryPage = location.pathname.includes("/history");
  const isRestartPage = location.pathname === "/game/restart";
  const isHowToPlayPage = location.pathname === "/game/help";
  const isContactPage = location.pathname === "/contact";
  const isProfilePage = location.pathname === "/profile";

  const helpScreens = React.useMemo(
    () => HELP_SCREEN_IDS.map((id, index) => ({ index, title: t(`help.screens.${id}.title`) })),
    [t]
  );

  return (
    <div css={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div css={menuHeaderStyle}>
        <h2 css={menuTitleStyle}>{t("menu.title")}</h2>
        <button css={closeButtonStyle} onClick={onClose} aria-label={t("menu.closeMenu")}>
          <CloseSVG />
        </button>
      </div>

      {/* Navigation Items */}
      <nav css={{ flex: 1, overflowY: "auto" }}>
        {/* Main Navigation */}
        <div css={menuSectionStyle}>
          <div css={sectionHeaderStyle}>{t("menu.sections.navigation")}</div>

          <Link to="/" css={menuItemStyle} className={isHomePage ? "active" : ""} onClick={onClose}>
            <HomeSVG />
            <span css={menuItemTextStyle}>{t("menu.items.home")}</span>
          </Link>

          {profileStatsEnabled && (
            <Link to="/profile" css={menuItemStyle} className={isProfilePage ? "active" : ""} onClick={onClose}>
              <StatsSVG />
              <span css={menuItemTextStyle}>{t("menu.items.profileStats")}</span>
            </Link>
          )}

          {isInGame && !isInMultiplayer && (
            <Link to="/game" css={menuItemStyle} className={isGameBoardPage ? "active" : ""} onClick={onClose}>
              <GameSVG />
              <span css={menuItemTextStyle}>{t("menu.items.resumeGame")}</span>
            </Link>
          )}

          {isInMultiplayer && (
            <Link to="/multiplayer" css={menuItemStyle} onClick={onClose}>
              <GameSVG />
              <span css={menuItemTextStyle}>{t("menu.items.multiplayerLobby")}</span>
            </Link>
          )}
        </div>

        <div css={dividerStyle} />

        {/* Game Actions - Only show when in single-player game */}
        {isInGame && !isInMultiplayer && (
          <>
            <div css={menuSectionStyle}>
              <div css={sectionHeaderStyle}>{t("menu.sections.singlePlayer")}</div>

              <Link
                to="/game/settings"
                css={menuItemStyle}
                className={isSettingsPage ? "active" : ""}
                onClick={onClose}
              >
                <SettingsSVG />
                <span css={menuItemTextStyle}>{t("menu.items.settings")}</span>
              </Link>

              <Link to="/game/history" css={menuItemStyle} className={isHistoryPage ? "active" : ""} onClick={onClose}>
                <HistorySVG />
                <span css={menuItemTextStyle}>{t("menu.items.moveHistory")}</span>
              </Link>

              <Link to="/game/restart" css={menuItemStyle} className={isRestartPage ? "active" : ""} onClick={onClose}>
                <RestartSVG />
                <span css={menuItemTextStyle}>{t("menu.items.restartGame")}</span>
              </Link>
            </div>

            <div css={dividerStyle} />
          </>
        )}

        {/* Multiplayer Game Actions - Only show when in multiplayer game */}
        {isInMultiplayer &&
          location.pathname.includes("/game/") &&
          (() => {
            // Extract base game path by removing /settings or /history if present
            const basePath = location.pathname.replace(/\/(settings|history)$/, "");
            return (
              <>
                <div css={menuSectionStyle}>
                  <div css={sectionHeaderStyle}>{t("menu.sections.multiplayer")}</div>

                  <Link
                    to={`${basePath}/settings`}
                    css={menuItemStyle}
                    className={isSettingsPage ? "active" : ""}
                    onClick={onClose}
                  >
                    <SettingsSVG />
                    <span css={menuItemTextStyle}>{t("menu.items.settings")}</span>
                  </Link>

                  <Link
                    to={`${basePath}/history`}
                    css={menuItemStyle}
                    className={isHistoryPage ? "active" : ""}
                    onClick={onClose}
                  >
                    <HistorySVG />
                    <span css={menuItemTextStyle}>{t("menu.items.moveHistory")}</span>
                  </Link>
                </div>

                <div css={dividerStyle} />
              </>
            );
          })()}

        {/* Help & Info */}
        <div css={menuSectionStyle}>
          <div css={sectionHeaderStyle}>{t("menu.sections.help")}</div>

          {!isOnHelpPage && (
            <Link to="/game/help" css={menuItemStyle} className={isHowToPlayPage ? "active" : ""} onClick={onClose}>
              <HelpSVG />
              <span css={menuItemTextStyle}>{t("menu.items.howToPlay")}</span>
            </Link>
          )}

          {/* Show individual help screens when on help page */}
          {isOnHelpPage && (
            <>
              {helpScreens.map((screen) => {
                const isActiveHelpScreen =
                  location.pathname === `/game/help/${screen.index}` ||
                  (screen.index === 0 && location.pathname === "/game/help");
                return (
                  <Link
                    key={screen.index}
                    to={`/game/help/${screen.index}`}
                    css={menuItemStyle}
                    className={isActiveHelpScreen ? "active" : ""}
                    onClick={onClose}
                  >
                    <HelpSVG />
                    <span css={menuItemTextStyle}>{screen.title}</span>
                  </Link>
                );
              })}
            </>
          )}

          <Link to="/contact" css={menuItemStyle} className={isContactPage ? "active" : ""} onClick={onClose}>
            <HistorySVG />
            <span css={menuItemTextStyle}>{t("menu.items.feedback")}</span>
          </Link>
        </div>
      </nav>

      {/* Footer */}
      <div
        css={{
          padding: "16px 20px",
          borderTop: "1px solid #e0e0e0",
          fontSize: "12px",
          color: "#666",
          textAlign: "center"
        }}
      >
        {gameMode === "multiplayer" && (
          <div css={{ marginBottom: "8px", fontWeight: "600", color: "#1976d2" }}>
            {t("menu.footer.multiplayerActive")}
          </div>
        )}
        <div>{t("menu.footer.tagline")}</div>
      </div>
    </div>
  );
};

export default AppMenu;
